import React, { useCallback } from 'react';
import { Platform, ScrollView } from 'react-native';
import { type Edges, SafeAreaView } from 'react-native-safe-area-context';
import { getIosModalPresentationConfig, useResponsive } from '@sg/garnish';
import { type DietaryPropertyKind } from '@sg/graphql-schema';
import { MenuDietaryPreferences } from '@sg/shared-fe/features/menu';

import { useDietaryRestrictionsMachine } from '../../../DietaryRestrictions';
import { Modal } from '../../../Modal';

// ─────────────────────────────────────────────────────────────────────────────

export const MenuContentDietaryPreferencesModal = (
  props: MenuContentDietaryPreferencesModalProps,
) => {
  const { isVisible, onClose } = props;

  const { match } = useResponsive();

  const {
    sortedRestrictionsNames,
    restrictions,
    isInitializingRestrictions,
    isModified,
    isUpdatingRestrictions,
    toggleRestriction,
    revertRestrictions,
    confirmRestrictions,
  } = useDietaryRestrictionsMachine({ referrer: 'Menu' });

  // ─── Helpers ─────────────────────────────────────────────────────────

  const resetAndClose = useCallback(() => {
    revertRestrictions();
    onClose();
  }, [onClose, revertRestrictions]);

  const confirmAndClose = useCallback(() => {
    confirmRestrictions();
    onClose();
  }, [confirmRestrictions, onClose]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Modal
      visible={isVisible}
      size={match(['full', 'medium'])}
      fitHeight
      onRequestClose={resetAndClose}
      {...getIosModalPresentationConfig()}
    >
      <ScrollView>
        <SafeAreaView edges={SAFE_AREA_EDGES}>
          <MenuDietaryPreferences.Container>
            <MenuDietaryPreferences.ModalHeader>
              <MenuDietaryPreferences.ModalCloseButton
                onPress={resetAndClose}
              />
            </MenuDietaryPreferences.ModalHeader>

            <MenuDietaryPreferences.Heading />
            <MenuDietaryPreferences.Description />

            <MenuDietaryPreferences.ButtonsContainer>
              {sortedRestrictionsNames.map((restriction) => (
                <MenuDietaryPreferences.Button<DietaryPropertyKind>
                  key={restriction}
                  restriction={restriction}
                  isLoading={isInitializingRestrictions}
                  isChecked={restrictions[restriction]}
                  isDisabled={isUpdatingRestrictions}
                  toggleRestriction={toggleRestriction}
                />
              ))}
            </MenuDietaryPreferences.ButtonsContainer>

            <MenuDietaryPreferences.Notices />

            <MenuDietaryPreferences.SubmitButton
              isLoading={isUpdatingRestrictions}
              isDisabled={!isModified}
              onPress={confirmAndClose}
            />
          </MenuDietaryPreferences.Container>
        </SafeAreaView>
      </ScrollView>
    </Modal>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const SAFE_AREA_EDGES: Edges = Platform.select({
  android: ['top'],
  default: [],
});

// ─── Types ───────────────────────────────────────────────────────────────────

type MenuContentDietaryPreferencesModalProps = {
  isVisible: boolean;
  onClose: () => void;
};
